/** Visão geral: placar por loja (nível do próprio marketplace) + instruções mais repetidas (corrigir em lote). */
import {
  Box, Button, Card, CardContent, Checkbox, Chip, FormControlLabel, Grid, Link, Stack, Table, TableBody, TableCell,
  TableHead, TableRow, Typography,
} from '@mui/material'
import { NIVEIS, NIVEL_COR, NIVEL_LABEL, topInstrucoes } from './logic'
import type { ValidadorDados } from './types'

const nf = (n: number | null | undefined) => (n == null ? '—' : n.toLocaleString('pt-BR'))

export function AbaVisaoGeral({
  dados, comercial, onComercial, onVerLoja, onVerInstrucao,
}: {
  dados: ValidadorDados
  comercial: boolean
  onComercial: (v: boolean) => void
  onVerLoja: (chave: string) => void
  onVerInstrucao: (texto: string) => void
}) {
  const status = new Map((dados.meta.canais_status || []).map((c) => [c.chave, c]))
  const top = topInstrucoes((dados.qualidade || []).filter((a) => a.estado !== 'ignorado'), comercial).slice(0, 12)
  const semIntegracao = (dados.meta.canais_status || []).filter((c) => !c.ok && !(dados.resumo_lojas || []).some((r) => r.chave === c.chave))
  return (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">
        Nível de qualidade dado pelo próprio marketplace. Mercado Livre dá nota 0–100; Shopee dá nível (A melhorar → Excelente);
        TikTok e Amazon não dão nota pela API, só instruções; Shein não dá nem nota nem instrução.
      </Typography>
      <Grid container spacing={1.5}>
        {(dados.resumo_lojas || []).map((r) => {
          const st = status.get(r.chave)
          const falhou = st && !st.ok
          const total = Object.values(r.por_nivel).reduce((s, n) => s + (n || 0), 0) || 1
          const temNivel = NIVEIS.some((k) => k !== 'sem' && r.por_nivel[k])
          return (
            <Grid key={r.chave} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 1 }}>
                    <Typography fontWeight={700}>{r.rotulo}</Typography>
                    {falhou ? <Chip size="small" label="sem leitura" variant="outlined" /> : <Chip size="small" label={r.marketplace} />}
                  </Stack>
                  {falhou ? <Typography variant="caption" color="error.main" sx={{ display: 'block', mb: 1 }}>Última análise falhou: {st?.erro}</Typography> : null}
                  {st?.erro_qualidade ? <Typography variant="caption" color="warning.main" sx={{ display: 'block', mb: 1 }}>Qualidade não lida na última análise: {st.erro_qualidade}</Typography> : null}
                  {temNivel ? (
                    <>
                      <Box role="img" aria-label="distribuição por nível" sx={{ display: 'flex', height: 10, borderRadius: 5, overflow: 'hidden', bgcolor: 'action.hover', mb: 1 }}>
                        {NIVEIS.map((k) => r.por_nivel[k] ? (
                          <Box key={k} title={`${NIVEL_LABEL[k]}: ${r.por_nivel[k]}`} sx={{ width: `${((r.por_nivel[k] || 0) / total) * 100}%`, bgcolor: NIVEL_COR[k] === 'default' ? 'divider' : `${NIVEL_COR[k]}.main` }} />
                        ) : null)}
                      </Box>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
                        {NIVEIS.filter((k) => r.por_nivel[k]).map((k) => <Chip key={k} size="small" color={NIVEL_COR[k]} variant="outlined" label={`${NIVEL_LABEL[k]} ${r.por_nivel[k]}`} />)}
                      </Stack>
                    </>
                  ) : <Chip size="small" variant="outlined" label="sem nota na API" sx={{ mb: 1 }} />}
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto', rowGap: 0.25, fontSize: 13, '& > :nth-of-type(even)': { textAlign: 'right', fontWeight: 600, fontVariantNumeric: 'tabular-nums' } }}>
                    <span>Anúncios lidos</span><span>{nf(r.lidos)}</span>
                    {r.nota_media != null ? (<><span>Nota média</span><span>{r.nota_media}</span></>) : null}
                    {r.sem_nota_calculada ? (<><span>Nota ainda não calculada</span><span>{nf(r.sem_nota_calculada)}</span></>) : null}
                    <span>Com o que melhorar</span><span>{nf(r.com_pendencia)}</span>
                    <span>Com alerta</span><span>{nf(r.com_alerta)}</span>
                    <span>Com divergência</span><span>{nf(r.com_divergencia)}</span>
                  </Box>
                  <Button size="small" variant="outlined" sx={{ mt: 1.25 }} onClick={() => onVerLoja(r.chave)}>Ver anúncios</Button>
                </CardContent>
              </Card>
            </Grid>
          )
        })}
        {semIntegracao.map((c) => (
          <Grid key={c.chave} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
                <Stack direction="row" justifyContent="space-between"><Typography fontWeight={700}>{c.loja}</Typography><Chip size="small" variant="outlined" label="sem leitura" /></Stack>
                <Typography variant="caption" color="text.secondary">{c.erro}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Box>
        <Typography variant="h6" sx={{ fontSize: 16 }}>Instruções mais repetidas</Typography>
        <Typography variant="caption" color="text.secondary">Corrigir em lote: clique para ver os anúncios com aquela instrução.</Typography>
        <Box sx={{ width: '100%', overflowX: 'auto', mt: 1 }}>
          <Table size="small" sx={{ '& th': { fontSize: 11, textTransform: 'uppercase', letterSpacing: '.04em', color: 'text.secondary', bgcolor: 'action.hover' } }}>
            <TableHead><TableRow><TableCell>Instrução do marketplace</TableCell><TableCell>Tipo</TableCell><TableCell>Lojas</TableCell><TableCell align="right">Anúncios</TableCell></TableRow></TableHead>
            <TableBody>
              {top.map((t) => (
                <TableRow key={t.texto} hover>
                  <TableCell><Link component="button" underline="hover" onClick={() => onVerInstrucao(t.texto)} sx={{ textAlign: 'left' }}>{t.texto}</Link></TableCell>
                  <TableCell><Chip size="small" variant="outlined" color={t.modo === 'alerta' ? 'error' : 'info'} label={t.modo === 'alerta' ? 'Alerta' : 'Oportunidade'} /></TableCell>
                  <TableCell><Typography variant="caption">{t.lojas.join(', ')}</Typography></TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{nf(t.n)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
        <FormControlLabel sx={{ mt: 1 }} control={<Checkbox size="small" checked={comercial} onChange={(e) => onComercial(e.target.checked)} />}
          label={<Typography variant="caption">Mostrar sugestões comerciais do ML (frete grátis, parcelamento, preço, promoções, ads, Flex) — ficam fora por padrão, como no Score</Typography>} />
      </Box>
    </Stack>
  )
}
